/**
 * 配置数据字典 Store
 * 用于全局管理系统配置参数
 * 数据会持久化到本地存储
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getDataDictionary, type DataDictionaryItem } from '@/api/config';
import { setRuntimeJouleDecimals } from '@/common/utils/money';

const STORAGE_KEY = 'app_config_dictionary';

export const useConfigStore = defineStore('config', () => {
  // 状态
  const dictionary = ref<DataDictionaryItem[]>([]);
  const isLoading = ref(false);
  const isInitialized = ref(false);
  const error = ref<string | null>(null);

  /**
   * 从本地存储加载配置
   */
  const loadFromStorage = (): boolean => {
    try {
      const stored = uni.getStorageSync(STORAGE_KEY);
      if (stored) {
        dictionary.value = JSON.parse(stored);
        isInitialized.value = true;
        const decItem = dictionary.value.find((d) => d.name === 'PLATFORM_COIN_DECIMALS');
        if (decItem?.value != null) {
          setRuntimeJouleDecimals(Number(decItem.value));
        }
        return true;
      }
    } catch (err) {
      // Load from storage failed
    }
    return false;
  };

  /**
   * 保存配置到本地存储
   */
  const saveToStorage = () => {
    try {
      uni.setStorageSync(STORAGE_KEY, JSON.stringify(dictionary.value));
    } catch (err) {
      // Save to storage failed
    }
  };

  /**
   * 清除本地存储的配置
   */
  const clearStorage = () => {
    try {
      uni.removeStorageSync(STORAGE_KEY);
      dictionary.value = [];
      isInitialized.value = false;
    } catch (err) {
      // Clear storage failed
    }
  };

  // 计算属性：节点购买金额
  const nodePayAmount = computed(() => {
    const item = dictionary.value.find(d => d.name === 'NODE_PAY_AMOUNT');
    return item ? item.value : '1000'; // 默认值 1000
  });

  /**
   * 提现手续费绝对数量（旧 UI 兼容）。
   * Business API 使用 bps，见 withdrawFeeBps；新 UI 应优先用 bps 估算。
   */
  const withdrawFee = computed(() => {
    const item = dictionary.value.find(d => d.name === 'WITHDRAW_POUNDAGE_QUANTITY');
    return item ? item.value : '0';
  });

  /** 提现手续费 bps（如 500 = 5%），来自 WITHDRAW_FEE_BPS */
  const withdrawFeeBps = computed(() => {
    const item = dictionary.value.find(d => d.name === 'WITHDRAW_FEE_BPS');
    const n = item ? Number(item.value) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  });

  // 计算属性：最小提现数量
  const withdrawMinQuantity = computed(() => {
    const item = dictionary.value.find(d => d.name === 'WITHDRAW_MIN_QUANTITY');
    return item ? item.value : '10'; // 默认 10 USDT，后端配置加载后覆盖
  });

  /** 平台币（Joule）账本精度：J18=18；未加载时默认 18 */
  const jouleDecimals = computed(() => {
    const item = dictionary.value.find((d) => d.name === 'PLATFORM_COIN_DECIMALS');
    const n = item ? Number(item.value) : 18;
    return Number.isFinite(n) && n >= 0 ? n : 18;
  });

  /**
   * 根据名称获取配置值
   */
  const getConfigValue = (name: string, defaultValue: string = ''): string => {
    const item = dictionary.value.find(d => d.name === name);
    const raw = item ? item.value : defaultValue;
    // 旧品牌残留（本地缓存/后端文案）统一替换
    if (typeof raw === 'string' && /Workerspace|Worker\s*Space|workerspace/i.test(raw)) {
      if (name === 'TERM_OF_SERVICE' || name === 'PRIVACY_POLICY' || name === 'SUPPORT_EMAIL') {
        return defaultValue;
      }
      return raw.replace(/Workerspace|Worker\s*Space|workerspace/gi, 'PANTA MARKET');
    }
    return raw;
  };

  /**
   * 加载配置：platform-coin + withdrawals/config（不再请求 /v1/data-dictionary）
   */
  const loadDictionary = async (): Promise<boolean> => {
    if (isLoading.value) return false;
    
    isLoading.value = true;
    error.value = null;

    try {
      const data = await getDataDictionary();
      dictionary.value = data;
      isInitialized.value = true;
      saveToStorage();
      return true;
    } catch (err: any) {
      // 新后端无字典接口时用本地默认值继续
      error.value = err?.message || 'Failed to load config';
      isInitialized.value = true;
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 初始化：优先从本地存储加载，不主动从 API 加载（需要登录后调用 loadDictionary）
   */
  const init = async (): Promise<boolean> => {
    // 如果已初始化，直接返回
    if (isInitialized.value && dictionary.value.length > 0) {
      return true;
    }
    // 尝试从本地存储加载
    return loadFromStorage();
  };

  /**
   * 刷新配置（强制从 API 重新加载）
   */
  const refresh = async (): Promise<boolean> => {
    return loadDictionary();
  };

  return {
    // 状态
    dictionary,
    isLoading,
    isInitialized,
    error,
    // 计算属性
    nodePayAmount,
    withdrawFee,
    withdrawFeeBps,
    withdrawMinQuantity,
    jouleDecimals,
    // 方法
    getConfigValue,
    loadDictionary,
    init,
    refresh,
    clearStorage,
  };
});
